
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export interface KeywordSuggestion {
  keyword: string;
  searchVolume: number;
  difficulty: number;
  cpc: number;
  competition: number;
  trend?: number[];
  relatedKeywords?: string[];
  country?: string;
}

export const useKeywordAssistant = (brandId: string | null) => {
  const [suggestions, setSuggestions] = useState<KeywordSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentCountry, setCurrentCountry] = useState<string>('');

  const clearSuggestions = () => {
    setSuggestions([]);
  };

  const searchKeywords = async (keywords: string[], countryCode: string, accumulate: boolean = false) => {
    if (!brandId) {
      toast.error("No brand selected");
      return;
    }

    setIsLoading(true);
    try {
      console.log("Searching keywords for keywords:", keywords, "country:", countryCode, "brandId:", brandId, "accumulate:", accumulate);
      
      // Get country name from brand's targeted countries
      const { data: brandData } = await supabase
        .from('brand_data')
        .select('targeted_countries')
        .eq('brand_id', brandId)
        .single();

      const targetedCountries = brandData?.targeted_countries;
      let countryName = 'Unknown';
      
      if (Array.isArray(targetedCountries)) {
        const selectedCountry = targetedCountries.find((c: any) => {
          return c && typeof c === 'object' && 'code' in c && c.code?.toString() === countryCode;
        });
        countryName = (selectedCountry as any)?.name || 'Unknown';
      }
      
      const { data, error } = await supabase.functions.invoke('dataforseo-keyword-research', {
        body: {
          keywords: keywords,
          brandId: brandId,
          locationCode: parseInt(countryCode)
        }
      });

      if (error) {
        console.error("Error searching keywords:", error);
        throw error;
      }

      if (data && data.results) {
        // Sort by search volume descending and take top 30
        const sortedResults = data.results
          .sort((a: any, b: any) => (b.searchVolume || 0) - (a.searchVolume || 0))
          .slice(0, 30)
          .map((result: any) => {
            console.log("Processing result:", result);
            
            // Extract monthly search data for trend - use the trend data that's already processed
            let trendData: number[] = [];
            if (result.trend && Array.isArray(result.trend) && result.trend.length > 0) {
              trendData = result.trend;
              console.log(`Using processed trend data for ${result.keyword}:`, trendData);
            } else if (result.monthlySearches && Array.isArray(result.monthlySearches)) {
              // Fallback: Sort by year and month to get chronological order
              const sortedMonthly = result.monthlySearches
                .sort((a: any, b: any) => {
                  if (a.year !== b.year) return a.year - b.year;
                  return a.month - b.month;
                })
                .slice(-12); // Get last 12 months
              
              trendData = sortedMonthly.map((monthly: any) => monthly.search_volume || 0);
              console.log(`Using monthly searches for trend data for ${result.keyword}:`, trendData);
            }

            return {
              keyword: result.keyword,
              searchVolume: result.searchVolume || 0,
              difficulty: result.difficulty || 0,
              cpc: result.cpc || 0,
              competition: result.competition || 0,
              trend: trendData, // Make sure we include the trend data
              relatedKeywords: result.relatedKeywords || [], // Include related keywords
              country: countryName // Include country name
            };
          });

        console.log("Final processed results:", sortedResults);
        
        // Either accumulate results or replace
        if (accumulate) {
          setSuggestions(prev => [...prev, ...sortedResults]);
        } else {
          setSuggestions(sortedResults);
        }
        
        setCurrentCountry(countryName); // Store current country
        toast.success(`Found ${sortedResults.length} keyword suggestions for ${countryName}`);
      } else if (!accumulate) {
        setSuggestions([]);
        toast.info("No keyword suggestions found");
      }
    } catch (error) {
      console.error("Error in keyword search:", error);
      toast.error("Failed to search keywords. Please try again.");
      if (!accumulate) {
        setSuggestions([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const addSelectedKeywords = async (selectedKeywords: string[]) => {
    if (!brandId) {
      toast.error("No brand selected");
      return;
    }

    try {
      console.log("=== ADDING SELECTED KEYWORDS ===");
      console.log("Selected keywords:", selectedKeywords);
      console.log("Available suggestions:", suggestions);
      console.log("Current country:", currentCountry);

      // Prepare keyword results data
      const keywordResults = suggestions
        .filter(suggestion => selectedKeywords.includes(suggestion.keyword))
        .map(suggestion => ({
          brand_id: brandId,
          keyword: suggestion.keyword,
          search_volume: suggestion.searchVolume,
          difficulty: suggestion.difficulty,
          cpc: suggestion.cpc,
          competition: suggestion.competition,
          trend: suggestion.trend || [],
          related_keywords: suggestion.relatedKeywords || [],
          country: suggestion.country || currentCountry || null,
          source: 'Keyword Assistant'
        }));

      console.log("Keyword results prepared:", keywordResults);

      if (keywordResults.length === 0) {
        toast.error("No matching keyword data found to save");
        return;
      }

      // Check for existing keyword + country combinations
      const keywordsToCheck = keywordResults.map(kr => kr.keyword);
      const countryToCheck = keywordResults[0].country || null;

      console.log("Checking for existing keywords:", keywordsToCheck, "in country:", countryToCheck);

      const { data: existingKeywords, error: checkError } = await supabase
        .from('keyword_research_results')
        .select('keyword, country')
        .eq('brand_id', brandId)
        .in('keyword', keywordsToCheck);

      if (checkError) {
        console.error("Error checking existing keywords:", checkError);
        throw checkError;
      }

      console.log("Existing keywords found:", existingKeywords);

      // Filter out keywords that already exist with same country
      const existingSet = new Set(
        existingKeywords
          ?.filter(ek => ek.country === countryToCheck)
          ?.map(ek => ek.keyword) || []
      );

      const newKeywordResults = keywordResults.filter(kr => !existingSet.has(kr.keyword));
      const skippedCount = keywordResults.length - newKeywordResults.length;

      console.log("New keywords to save:", newKeywordResults);
      console.log("Skipped count:", skippedCount);

      // Save only new keywords
      if (newKeywordResults.length > 0) {
        console.log("Attempting to save to database...");
        const { error: resultsError, data: insertData } = await supabase
          .from('keyword_research_results')
          .insert(newKeywordResults)
          .select();

        if (resultsError) {
          console.error("Database error saving keyword results:", resultsError);
          throw resultsError;
        }

        console.log("Successfully saved to database:", insertData);
      }

      // Create appropriate success message
      let message = "";
      if (newKeywordResults.length > 0 && skippedCount > 0) {
        message = `Added ${newKeywordResults.length} keywords, ${skippedCount} skipped as existed`;
      } else if (newKeywordResults.length > 0) {
        message = `Added ${newKeywordResults.length} keywords to research results`;
      } else if (skippedCount > 0) {
        message = `${skippedCount} keywords skipped as they already exist`;
      }

      toast.success(message);
      
    } catch (error) {
      console.error("Error adding keywords:", error);
      toast.error("Failed to add keywords. Please try again.");
    }
  };

  return {
    suggestions,
    isLoading,
    searchKeywords,
    addSelectedKeywords,
    clearSuggestions
  };
};
